import { useCallback, useMemo } from 'react'
import type { ComponentProps, ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import NestableClickable from 'components/Common/NestableClickable'
import Image from 'components/Common/Image/Image'
import getResizableImage from 'components/OfferList/OfferCards/getResizableImage'
import type CarouselCardMedium from 'components/OfferList/OfferCards/OfferCardMedium/CarouselCardMedium'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Colour from 'components/utils/Colour'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import SolidLockAltIcon from 'LuxKit/Icons/solid/SolidLockAltIcon'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { LabelCountProvider } from 'contexts/LabelCountContext'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import { earlyAccessRestrictedToMembers } from 'luxPlus/lib/utils'
import LuxPlusHiddenOfferLabel from './LuxPlusHiddenOfferLabel'
import useHiddenOfferClickHandler from './useHiddenOfferClickHandler'

const defaultThemeClass = getThemeClassName('default')
const memberPriceOptions = { showMemberPrice: true }

const Card = styled(NestableClickable)`
  display: grid;
  grid-template-rows: auto 1fr;
  gap: ${rem(16)};
  height: 100%;
  min-height: ${rem(400)};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(520)};
  }

  &.variant-opaque {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }

  &.hidden {
    display: none;
  }

  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: var(--border-radius-m);
    overflow: hidden;
  }
`

const ImageContainer = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;

  > picture {
    display: block;
    width: 100%;
    height: 100%;
  }

  body.${REBRAND_PALETTE_CLASS} &.rounded {
    border-radius: var(--border-radius-m);
  }
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(12px);
  color: var(--palette-neutral-surface-dark-contrast);
`

const Details = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${rem(12)};

  &.variant-opaque {
    padding-block: 0 ${rem(16)};
    padding-inline: ${rem(16)};
  }
`

interface Props extends Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'> {
  offer: App.HotelOffer | App.HotelOfferSummary
  className?: string
  urgencyLabels?: Array<App.OfferUrgencyLabel>
  inclusions?: ReactNode
}

function LuxPlusHiddenOfferCarouselCardMedium({ offer, className, urgencyLabels, inclusions, variant = 'default', eagerLoadImage }: Props) {
  const intl = useIntl()
  const onClick = useHiddenOfferClickHandler(offer)
  const accentColour = useRebrandSuccessDarkColour()
  const image = useMemo(() => getResizableImage(offer.image), [offer.image])
  const { valueOffPercent } = useOfferPackagePrice(offer.lowestPricePackage!, offer, memberPriceOptions)
  const location = useMemo(() => {
    if (offer.locationHeading === offer.locationSubheading) return offer.locationSubheading
    return intl.formatMessage({
      id: 'JPIdHoddJ6AZJhc7qv-3',
      defaultMessage: '{locationHeading}, {locationSubheading}',
      description: 'LuxPlus hidden offer location heading and subheading separated by comma',
    }, { locationHeading: offer.locationHeading, locationSubheading: offer.locationSubheading })
  }, [intl, offer.locationHeading, offer.locationSubheading])
  const highlighted = useCallback((chunks: Array<ReactNode>) => <Colour value={accentColour}>{chunks}</Colour>, [accentColour])
  const heading = useMemo(() => intl.formatMessage({
    id: '13zt5tQpDW-4cLzeQzg4',
    defaultMessage: '<highlighted>Members save up to {valueOffPercent}%</highlighted> on this hidden offer in {location}',
    description: 'Heading showing member savings percentage on hidden hotel offer',
  }, { valueOffPercent, location, highlighted }), [intl, valueOffPercent, location, highlighted])
  const isEarlyAccess = earlyAccessRestrictedToMembers(offer)

  return <LabelCountProvider maxLabels={2}>
    <Card
      className={clsx('lux-plus-hidden-offer-card', className, `variant-${variant}`, variant === 'opaque' && defaultThemeClass)}
      onClick={onClick}
    >
      <ImageContainer className={clsx(defaultThemeClass, variant === 'default' && 'rounded')}>
        <Image
          aspectRatio="16:9"
          image={image}
          fit="center"
          quality="low"
          width={512}
          height={288}
          dpr={2}
          loading={eagerLoadImage ? 'eager' : 'lazy'}
          fetchPriority={eagerLoadImage ? 'high' : undefined}
          alt={intl.formatMessage({
            id: 'kCTgub4earGNxPXIxs0E',
            defaultMessage: 'Exclusive hidden offer in {location}',
            description: 'Alt text for a LuxPlus hidden offer carousel card image',
          }, { location: offer.locationSubheading })}
        />
        <Overlay><SolidLockAltIcon size="XL" /></Overlay>
      </ImageContainer>
      <Details className={`variant-${variant}`}>
        <VerticalSpacer gap={8}>
          {!isEarlyAccess && <LuxPlusHiddenOfferLabel />}
          {isEarlyAccess && <OfferLabels offer={offer} urgencyLabels={urgencyLabels} />}
          <Heading variant="heading5">{heading}</Heading>
          {inclusions}
        </VerticalSpacer>
        <div>
          <ProductPaletteProvider palette="lux-plus">
            <TextButton variant="default" kind="secondary" onClick={onClick}>
              {intl.formatMessage({
                id: 'cF0wqDB2oTUCvbESRvph',
                defaultMessage: 'Unlock this offer via Free Preview',
                description: 'CTA button text to unlock a hidden offer by starting LuxPlus+ free preview',
              })}
            </TextButton>
          </ProductPaletteProvider>
        </div>
      </Details>
    </Card>
  </LabelCountProvider>
}

export default LuxPlusHiddenOfferCarouselCardMedium
